import type { ReactNode } from 'react';
import type { ModuleFamily } from '../../config/moduleCatalog';

/**
 * A labelled box around one family's module rows on a topic page (feature 033, FR-017,
 * 2026-10-19). The Preempts page shows its Precision rows under "Non-competitive" and its
 * Competitive rows under "Competitive"; the heading says which side of the competition rule
 * the rows inside serve, and the box keeps the two sets apart on the eye.
 *
 * A group is presentation only: it has no Practice or Master of its own, no key and no
 * history — the user chose headings over practisable group rows. It is a `<section>` named
 * by its heading so assistive technology announces the group a row sits in, since the same
 * sub-category (Preemptive openings; Responding to partner's preempt) appears once in each.
 *
 * The box uses the page's own pieces: the hairline `border-line-strong` the rows use, the
 * cream page tint, and the serif ink heading over a short brass rule — the title line's rule
 * at half width, so the group reads as a sub-heading of the topic and not a second title.
 */
export interface TopicModuleGroupProps {
  family: ModuleFamily;
  heading: string;
  children: ReactNode;
}

export function TopicModuleGroup({ family, heading, children }: TopicModuleGroupProps) {
  const headingId = `module-group-${family}-heading`;
  return (
    <section
      aria-labelledby={headingId}
      // `pb-1.5`, not the rows' spacing: each row carries `mb-2.5`, so the last one already
      // leaves most of the bottom gap; this just stops its margin collapsing into the border.
      className="mb-6 rounded-xl border border-line-strong bg-cream px-3 pb-1.5 pt-4 narrow:px-2.5"
      data-testid={`module-group-${family}`}
    >
      <h2 id={headingId} className="font-serif text-[19px] leading-tight text-ink">
        {heading}
      </h2>
      <div className="mb-3.5 mt-2 h-0.5 w-8 bg-brass" aria-hidden="true" />
      {children}
    </section>
  );
}
